/**
 * LoginPage (B0.1) — Business operator authentication screen.
 *
 * Single-column tablet-first layout: centered card, max-width 480px.
 * Error messages appear inline (not toast) so operators on a shared
 * tablet don't miss them after a tap.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Envelope, LockSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { ApiError } from '../shared/api/apiClient';
import { useAuth } from '../shared/hooks/useAuth';
import { Button } from '../shared/ui/Button';
import { Card, CardBody } from '../shared/ui/Card';
import { FieldError } from '../shared/ui/FieldError';
import { Input } from '../shared/ui/Input';
import { Label } from '../shared/ui/Label';
import { cn } from '../shared/utils/cn';

const schema = z.object({
  email: z.string().min(1).email(),
  password: z.string().min(6),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const login = useAuth((s) => s.login);
  const isResolving = useAuth((s) => s.isResolving);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.email, values.password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setServerError(t('login.errorInvalid'));
        } else if (err.status === 423) {
          setServerError(t('login.errorLocked'));
        } else if (err.status === 403) {
          setServerError(t('login.errorNoAccess'));
        } else {
          setServerError(err.payload.detail || err.payload.title);
        }
      } else {
        setServerError(t('login.errorInvalid'));
      }
    }
  };

  return (
    <div
      className={cn(
        'min-h-screen flex items-center justify-center',
        'bg-biz-surface px-biz-card py-biz-section',
      )}
    >
      <Card className="w-full max-w-md">
        <CardBody className="space-y-biz-section">
          <header className="space-y-biz-content">
            <div
              aria-hidden="true"
              className="w-12 h-12 rounded-biz-card bg-biz-primary text-biz-primary-foreground flex items-center justify-center biz-heading font-bold text-biz-xl"
            >
              B
            </div>
            <h1 className="text-biz-2xl biz-heading font-semibold">
              {t('login.title')}
            </h1>
            <p className="text-biz-sm text-biz-muted-foreground">
              {t('login.subtitle')}
            </p>
          </header>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-biz-content-lg"
            noValidate
          >
            <div>
              <Label htmlFor="email">{t('login.emailLabel')}</Label>
              <div className="relative">
                <Envelope
                  weight="regular"
                  size={20}
                  aria-hidden="true"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-muted-foreground"
                />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder={t('login.emailPlaceholder')}
                  invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  className="pl-10"
                  {...register('email')}
                />
              </div>
              <FieldError id="email-error">
                {errors.email?.message}
              </FieldError>
            </div>

            <div>
              <Label htmlFor="password">{t('login.passwordLabel')}</Label>
              <div className="relative">
                <LockSimple
                  weight="regular"
                  size={20}
                  aria-hidden="true"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-biz-muted-foreground"
                />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t('login.passwordPlaceholder')}
                  invalid={Boolean(errors.password)}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  className="pl-10"
                  {...register('password')}
                />
              </div>
              <FieldError id="password-error">
                {errors.password?.message}
              </FieldError>
            </div>

            {serverError ? (
              <div
                role="alert"
                className={cn(
                  'px-biz-content-lg py-biz-content rounded-biz-btn',
                  'bg-biz-danger-subtle text-biz-danger',
                  'border border-biz-danger/30',
                  'text-biz-sm',
                )}
              >
                {serverError}
              </div>
            ) : null}

            <Button
              type="submit"
              intent="primary"
              size="lg"
              fullWidth
              loading={isResolving}
            >
              {t('login.submit')}
            </Button>
          </form>

          <div className="flex items-center justify-center text-biz-sm text-biz-muted-foreground">
            <Link
              to="/signup"
              className="inline-flex items-center min-h-biz-touch px-biz-content-lg rounded-biz-btn focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring"
            >
              {t('login.signupLink', { defaultValue: 'Hesabın yok mu? Kayıt ol' })}
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
